import { IResourceType } from 'app/containers/Resource/slice/types';
import { IRecipeResource } from '../../Recipe/types';
import {
  ITaskAvailableResource,
  ITaskAvailableResourceChilden,
  ITaskAvailableResourceOption,
  ITaskPlannedResource,
  ITasksArrResponseItem,
  ITasksTableItem,
  ITaskUsedResource,
} from './types';
import * as L from 'leaflet';
import { FieldsBoundaries } from 'app/containers/Map/slice/types';
import i18next from 'i18next';

export const mapTasksResponseToTasksArr = (
  tasks: ITasksArrResponseItem[],
): ITasksTableItem[] => {
  const tasksArr: ITasksTableItem[] = tasks.map(
    ({
      fieldTasks,
      status,
      category,
      completedArea,
      totalArea,
      endDate,
      id,
      name,
    }) => {
      const cultures = Array.from(
        new Set(
          fieldTasks?.map(fieldTask =>
            fieldTask.fieldSeason.culture
              ? fieldTask.fieldSeason.culture?.name
              : '',
          ),
        ),
      );

      return {
        category,
        completedArea,
        totalArea,
        cultures,
        endDate,
        id,
        name,
        status,
      };
    },
  );
  return tasksArr;
};

export const getResourceOptions = (
  optionsArr: ITaskAvailableResourceOption[],
  takenResourcesArr: ITaskPlannedResource[] | ITaskUsedResource[],
): ITaskAvailableResourceOption[] => {
  return optionsArr.map(resourceGroup => {
    return {
      ...resourceGroup,
      children: resourceGroup.children.map(resourceItem => {
        let resourceItemChildren: ITaskAvailableResourceChilden[] | undefined =
          resourceItem.children;
        let isDisabled = Boolean(
          takenResourcesArr.find(
            resource => resource.resourceId === resourceItem.resource.id,
          ),
        );
        if (resourceItem.children) {
          isDisabled = false;
          resourceItemChildren = resourceItem.children.map(
            resourceItemVariation => {
              const isDisabledVariation = Boolean(
                takenResourcesArr.find(
                  resource =>
                    resource.resourceId === resourceItemVariation.resource.id,
                ),
              );
              return {
                ...resourceItemVariation,
                disabled: isDisabledVariation,
              };
            },
          );
        }

        return {
          ...resourceItem,
          disabled: isDisabled,
          children: resourceItemChildren,
        };
      }),
    };
  });
};

export const mapResourcesToOptionsGroupedByResourceType = ({
  resourceTypes,
  resources,
}: {
  resourceTypes: IResourceType[];
  resources: ITaskAvailableResource[][];
}): ITaskAvailableResourceOption[] => {
  const resourcesByTypeId: ITaskAvailableResourceOption[] = resourceTypes
    .map((type, idx) => {
      let children: ITaskAvailableResourceChilden[] = [];
      if (type.seed) {
        children = resources[idx]
          .map(resource => ({
            value: resource.cultureVariation?.culture.id || 0,
            label: resource.cultureVariation?.culture.name || '',
            children: [
              {
                value: resource.id,
                label: resource.cultureVariation?.name || '',
                resource,
              },
            ],
            resource,
          }))
          // group variations by culture
          .reduce((accumulator: ITaskAvailableResourceChilden[], item) => {
            const cultureIndex = accumulator.findIndex(
              groupedCulture => groupedCulture.value === item.value,
            );
            if (cultureIndex === -1) {
              accumulator.push(item);
            } else {
              accumulator[cultureIndex].children?.push(item.children[0]);
            }
            return accumulator;
          }, [])
          .sort((a, b) => a.label.localeCompare(b.label));
      } else {
        children = resources[idx].map(resource => ({
          value: resource.id,
          label: resource.name,
          resource,
        }));
      }

      return {
        value: type.id,
        label: type.name,
        children,
      };
    })
    .filter(resourceType => resourceType.label !== 'Derlius');

  return resourcesByTypeId;
};

export const findResourceById = ({
  resourceId,
  allResources,
}: {
  resourceId?: number;
  allResources: ITaskAvailableResource[];
}) => {
  if (!resourceId) {
    return;
  }
  const resource = allResources.find(
    possibleResource => possibleResource.id === resourceId,
  );
  return resource;
};

export const mapRecipeResoourcesToTaskPlannedResources = (
  recipeResources: IRecipeResource[],
): ITaskPlannedResource[] => {
  return recipeResources.map(resource => {
    const payload: ITaskPlannedResource = {
      resourceId: resource.id,
      name: resource.name,
      norm: resource.norm,
      recommendedNorm: resource.recommendedNorm,
      price: resource.price ?? undefined,
    };

    return payload;
  });
};

export const getTimeSpent = (d: number | null) => {
  if (!d) {
    return '-';
  }

  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  // const s = Math.floor((d % 3600) % 60);

  const hDisplay = h > 0 ? `${h} h` : '';
  const mDisplay = m > 0 ? `${m}min` : '';
  // const sDisplay = s > 0 ? `${s}s` : '';

  const resultArray = [];
  if (hDisplay) {
    resultArray.push(hDisplay);
  }
  if (mDisplay) {
    resultArray.push(mDisplay);
  }

  return resultArray.join(', ');
};

export const getPolygonCenterPosition = (coordinates?: FieldsBoundaries) =>
  coordinates ? L.polygon(coordinates).getBounds().getCenter() : undefined;

export const getTaskCulturesString = (cultures: (string | undefined)[]) => {
  if (!cultures.length) {
    return '-';
  }

  const culturesString = cultures
    .map(
      culture =>
        culture || i18next.t('Common.WithoutCulture', 'Without culture'),
    )
    .sort()
    .join(', ');
  return culturesString;
};
